import { useEffect, useMemo, useState } from "react";
import { getSamples } from "../api/clients/sampleClient";
import type { Sample, SampleGroup } from "../api/types";

export type Explore = {
  exploreKey: string;
  displayName: string | null;
};

type UseGetSamplesResult = {
  explores: Explore[];
  samplesCollection: Record<string, Sample[]>;
  defaultExplore: Explore | null;
  loading: boolean;
  error?: Error;
};

const customTitleMappings: Record<string, string> = {
  activity: "CRM & Fundraising Activity",
  all_emails: "Email Reporting",
  offering_from_contact: "Investor Management (Most Common)",
  position: "Investment Detail & Assets",
  transaction_new: "Transaction Detail",
  gl_data: "General Ledger",
};

const toDisplayTitle = (input: string): string => {
  if (customTitleMappings[input]) {
    return customTitleMappings[input];
  }

  let result = input.replace(
    /_([a-z])/g,
    (_match, letter: string) => " " + letter.toUpperCase(),
  );
  result = result.charAt(0).toUpperCase() + result.slice(1);
  return result;
};

export const useGetSamples = (): UseGetSamplesResult => {
  const [sampleGroups, setSampleGroups] = useState<SampleGroup[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(undefined);

    getSamples()
      .then((data) => {
        if (cancelled) return;
        setSampleGroups(data.explores);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error("Failed to fetch samples"));
        setSampleGroups(undefined);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const explores: Explore[] = useMemo(() => {
    const result: Explore[] = [];
    for (const group of sampleGroups ?? []) {
      if (group.key === "default") {
        continue;
      }

      const exploreParts: string[] = group.key.split(":");
      result.push({
        exploreKey: group.key,
        displayName: toDisplayTitle(exploreParts[1]),
      });
    }
    return result;
  }, [sampleGroups]);

  const defaultExplore: Explore | null = useMemo(() => {
    const defaultGroup = sampleGroups?.find((group) => group.key === "default");
    if (!defaultGroup) {
      return null;
    }

    return {
      exploreKey: defaultGroup.key,
      displayName: null,
    };
  }, [sampleGroups]);

  const samplesCollection: Record<string, Sample[]> = useMemo(() => {
    const collection: Record<string, Sample[]> = {};
    (sampleGroups ?? []).forEach((sampleGroup) => {
      collection[sampleGroup.key] = sampleGroup.samples;
    });
    return collection;
  }, [sampleGroups]);

  return {
    explores,
    samplesCollection,
    defaultExplore,
    loading,
    error,
  };
};
